import React, { useState, useEffect, useRef, useContext } from "react";
import styled from "styled-components";
import { QRCodeSVG } from "qrcode.react";
import LoaderComponent from "../Loader/Loader";
import toast from "react-hot-toast";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../context/firebase";
import { UserContext } from "../../context/UserContext";
import { useTranslation } from "../../hooks/useTranslation";

export default function DepositModal({ close }) {
  const [success, setSuccess] = useState(false);
  const [stage, setStage] = useState("select");
  // const { userBalance, updateUserBalance } = useContext(UserContext);
  const [coupon, setCoupon] = useState("");
  const [depositValue, setDepositValue] = useState(null);
  const [qrCodeValue, setQrCodeValue] = useState(
    "00020126360014br.gov.bcb.pix0114373197090001645204000053039865802BR5922OOBYOU TECNOLOGIA LTDA6014RIO DE JANEIRO62070503***63047315"
  );
  const [file, setFile] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [transactionData, setTransactionData] = useState(null);

  const {translate} = useTranslation();
  
  const calculateChecksum = (data) => {
    let crc = 0xffff; // Valor inicial
    const polynomial = 0x1021; // Polinômio CRC-16-CCITT-FALSE

    for (let i = 0; i < data.length; i++) {
      crc ^= data.charCodeAt(i) << 8;
      for (let j = 0; j < 8; j++) {
        if ((crc & 0x8000) !== 0) {
          crc = (crc << 1) ^ polynomial;
        } else {
          crc = crc << 1;
        }
        crc &= 0xffff; // Garante 16 bits
      }
    }

    return crc.toString(16).toUpperCase().padStart(4, "0");
  };
  const generatePixCode = (transactionValue) => {
    if (!transactionValue || transactionValue <= 0) {
      throw new Error("Valor de transação inválido. Deve ser maior que 0.");
    }
    const formattedValue = transactionValue.toFixed(2).replace(".", ".");
    const amountLength = formattedValue.length;
    const lengthIndicator = `54${amountLength.toString().padStart(2, "0")}`;

    const basePix =
      `00020126360014br.gov.bcb.pix011437319709000164520400005303986${lengthIndicator}${formattedValue}5802BR5922OOBYOU TECNOLOGIA LTDA6014RIO DE JANEIRO62070503***6304`.trim();
    const checksum = calculateChecksum(basePix);
    return `${basePix}${checksum}`;
  };

  const handleNext = async () => {
    if (!depositValue || depositValue <= 0) {
      toast.error(translate("depositModal.error.invalidTransactionValue"));
      return;
    }

    try {
      const token = localStorage.getItem("userToken");
      if (!token) {
        toast.error(translate("depositModal.loginRequired"));
        return;
      }

      const pixCode = generatePixCode(depositValue);
      setQrCodeValue(pixCode);
      setStage("BRL2");

      // Make deposit API call
      const depositResponse = await fetch(
        "https://userdeposit-qj6yfdqbiq-uc.a.run.app",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount: depositValue * 100,
            type: "PIX",
          }),
        }
      );

      if (!depositResponse.ok) {
        toast.error(translate("depositModal.error.depositApi"));
        setStage("BRL3");
        throw new Error(`Erro HTTP! status: ${depositResponse.status}`);
      }

      const data = await depositResponse.json();
      setTransactionData(data);
    } catch (error) {
      console.error(translate("depositModal.error.pixGeneration"), error);
      toast.error(translate("depositModal.error.pixGeneration"));
      setStage("BRL3");
    }
  };

  const handleConfirm = async () => {
    setStage("loading");

    try {
      const token = localStorage.getItem("userToken");
      if (!token) {
        toast.error(translate("depositModal.loginRequired"));
        return;
      }
      
      // Only handle receipt upload if file exists
      if (file) {
        const storageRef = ref(
          storage,
          `users/${transactionData.userId}/receipts/${transactionData.transactionId}.pdf`
        );
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        // Send receipt URL to API
        const receiptResponse = await fetch(
          "https://useradddepositreceipt-qj6yfdqbiq-uc.a.run.app",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              depositId: transactionData.transactionId,
              receiptUrl: downloadURL,
            }),
          }
        );

        if (!receiptResponse.ok) {
          toast.error(
            translate("depositModal.error.addingTransaction")
          );
        }
      }

      setSuccess(true);
      setStage("BRL3");
    } catch (error) {
      console.error(translate("depositModal.error.addingTransaction"), error);
      setStage("BRL3");
    }
  };

  const inputRef = useRef(null);

  useEffect(() => {
    // Only run this effect when we're on the BRL1 stage
    if (stage === "BRL1" && inputRef.current) {
      const inputElement = inputRef.current;

      const handleInput = (e) => {
        const currentValue = e.target.value.replace(/\D/g, ""); // Remove caracteres não numéricos
        const numericValue = parseInt(currentValue, 10);

        if (numericValue > 10000) {
          inputElement.value = depositValue ? `R$ ${depositValue}` : "";
        } else {
          setDepositValue(numericValue || null);
          inputElement.value = numericValue ? `R$ ${numericValue}` : "";
        }
      };

      inputElement.addEventListener("input", handleInput);

      return () => {
        inputElement.removeEventListener("input", handleInput);
      };
    }
  }, [stage, depositValue]); // Added stage to dependencies

  const handleCurrencySelect = (currency) => {
    setSelectedCurrency(currency);
    setStage(`${currency}1`);
  };

  const handleCryptoDeposit = async (type) => {
    setStage("loading");
    try {
      const token = localStorage.getItem("userToken");
      if (!token) {
        toast.error("Você precisa estar logado para fazer um depósito");
        return;
      }

      // Calculate amount based on type
      const amount = type === "USDT" ? depositValue * 100 : depositValue;

      const response = await fetch(
        // "https://userdeposit-qj6yfdqbiq-uc.a.run.app",
        "https://teste",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount: amount,
            type: type,
          }),
        }
      );

      if (!response.ok) {
        toast.error(`Erro ao fazer depósito, entre em contato com o suporte`);
        setStage(`${type}3`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSuccess(true);
      setTimeout(() => {
        setStage(`${type}3`);
      }, 1000);
    } catch (error) {
      console.error(`Erro ao processar depósito em ${type}:`, error);
      toast.error("Falha ao processar o depósito.");
      setStage(`${type}3`);
    }
  };

  return (
    <>
      <Overlay onClick={() => close(true)} />
      <Popup>
        <div
          style={{
            position: "absolute",
            top: "25px",
            right: "30px",
            zIndex: "1000",
            cursor: "pointer",
            fontWeight: "bold",
          }}
          onClick={() => close(true)}
        >
          X
        </div>
        {stage === "loading" && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <LoaderComponent />
          </div>
        )}
        {stage === "select" && (
          <>
            <Header style={{ textAlign: "center" }}>{translate("depositModal.selectCurrency")}</Header>
            <div
              style={{
                width: "100%",
                height: "5px",
                backgroundColor: "#c09c5c",
                marginBottom: "20px",
              }}
            ></div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "15px" }}
            >
              <CurrencyButton onClick={() => handleCurrencySelect("BRL")}>
                {translate("depositModal.currencySelection.brl")}
              </CurrencyButton>
              <CurrencyButton onClick={() => handleCurrencySelect("USDT")}>
                {translate("depositModal.currencySelection.usdt")}
              </CurrencyButton>
              <CurrencyButton onClick={() => handleCurrencySelect("BTC")}>
                {translate("depositModal.currencySelection.btc")}
              </CurrencyButton>
            </div>
          </>
        )}

        {stage === "BRL1" && (
          <>
            <Header style={{ textAlign: "center" }}>{translate("depositModal.brlDeposit")}</Header>
            <div
              style={{
                width: "100%",
                height: "5px",
                backgroundColor: "#c09c5c",
                marginBottom: "20px",
              }}
            ></div>

            <SubHeader style={{ textAlign: "center" }}>
              {translate("depositModal.depositValue")}
            </SubHeader>
            <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
              <ValueButton onClick={() => setDepositValue(depositValue + 250)}>
                {translate("depositModal.valueButton.add250")}
              </ValueButton>
              <ValueButton onClick={() => setDepositValue(depositValue + 1000)}>
                {translate("depositModal.valueButton.add1000")}
              </ValueButton>
              <ValueButton onClick={() => setDepositValue(depositValue + 5000)}>
                {translate("depositModal.valueButton.add5000")}
              </ValueButton>
            </div>

            <Input
              ref={inputRef}
              placeholder={translate("depositModal.inputPlaceholder")}
              value={depositValue ? `R$ ${depositValue}` : ""}
              type="text"
              inputMode="numeric"
              max="900000"
              pattern="[0-9]+"
            />

            <Button onClick={handleNext} style={{ backgroundColor: "#00C160" }}>
              {translate("depositModal.nextButton")}
            </Button>
          </>
        )}

        {stage === "BRL2" && (
          <>
            <Header style={{ textAlign: "center" }}>
              {translate("depositModal.brlDepositInstruction")}
            </Header>
            <div
              style={{
                width: "100%",
                height: "5px",
                backgroundColor: "#c09c5c",
                marginBottom: "20px",
              }}
            ></div>
            <SubHeader style={{ textAlign: "center" }}>
              {translate("depositModal.scanQrCode")}
            </SubHeader>
            <div
              style={{
                padding: "10px",
                background: "white",
                width: "212px",
                margin: "auto",
              }}
            >
              <QRCodeSVG value={qrCodeValue} size={192} />
            </div>
            <SubHeader style={{ textAlign: "center" }}></SubHeader>

            <ButtonCopy
              onClick={() => {
                navigator.clipboard.writeText(qrCodeValue);
                toast.success(translate("depositModal.toast.qrCodeCopied"));
              }}
            >
              {translate("depositModal.copyQrCode")}
            </ButtonCopy>

            <div
              style={{ width: "100%", textAlign: "center", marginTop: "1rem" }}
            >
              <SubHeader style={{ fontSize: "1rem", marginBottom: "10px" }}>
                {translate("depositModal.attachReceipt").replace("{value}", depositValue?.toFixed(2).replace(".", ","))}
              </SubHeader>

              <label
                htmlFor="receipt"
                style={{ cursor: "pointer", width: "100%" }}
              >
                <div
                  style={{
                    backgroundColor: "#00C1FF",
                    color: "white",
                    padding: "10px",
                    borderRadius: "5px",
                    display: "inline-block",
                    fontWeight: "bold",
                    width: "100%",
                  }}
                >
                  {file ? file.name : translate("depositModal.selectFile")}
                </div>
                <input
                  type="file"
                  id="receipt"
                  accept=".pdf,image/*"
                  style={{ display: "none" }}
                  value={file ? file.filename : ""}
                  onChange={(e) => {
                    const selectedFile = e.target.files[0];
                    if (selectedFile) {
                      setFile(selectedFile);
                    }
                  }}
                />
              </label>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <Button
                onClick={handleConfirm}
                style={{ backgroundColor: "#00C160", width: "100%" }}
              >
                {translate("depositModal.confirmButton")}
              </Button>
              <TermsText style={{ marginTop: "0.5rem" }}>
                <TermsLink
                  onClick={() => window.open("/terms-and-conditions", "_blank")}
                >
                  {translate("depositModal.termsAndConditions")}
                </TermsLink>
              </TermsText>
            </div>
          </>
        )}
        {stage === "BRL3" && (
          <>
            <Header
              style={{
                marginTop: "18rem",
                textAlign: "center",
              }}
            >
              {success ? (
                <>
                  {translate("depositModal.transactionSuccess")}
                </>
              ) : (
                <>
                  <span style={{ color: "red" }}>{translate("depositModal.transactionError")}</span>
                </>
              )}
            </Header>
            <Button onClick={close}>OK</Button>
          </>
        )}

        {stage === "BTC1" && (
          <>
            <Header style={{ textAlign: "center" }}>
              DEPÓSITO EM REDE BITCOIN
            </Header>
            <div
              style={{
                width: "100%",
                height: "5px",
                backgroundColor: "#c09c5c",
                marginBottom: "20px",
              }}
            ></div>
            <SubHeader style={{ textAlign: "center" }}>
              Leia o QR Code para fazer o depósito
            </SubHeader>
            <div
              style={{
                padding: "10px",
                background: "white",
                width: "212px",
                margin: "auto",
              }}
            >
              <QRCodeSVG
                value={"191PtBBGDB9xyoApTPsryhLF5TaeYu7rds"}
                size={192}
              />
            </div>

            <ButtonCopy
              onClick={() => {
                navigator.clipboard.writeText(
                  "191PtBBGDB9xyoApTPsryhLF5TaeYu7rds"
                );
                toast.success("Endereço Bitcoin copiado com sucesso!");
              }}
              style={{ backgroundColor: "#F7931A", border: "none" }}
            >
              COPIAR ENDEREÇO BITCOIN
            </ButtonCopy>

            <Button
              onClick={() => handleCryptoDeposit("BTC")}
              style={{ backgroundColor: "#F7931A" }}
            >
              CONFIRMAR DEPÓSITO
            </Button>
            <TermsText style={{ marginTop: "0.5rem" }}>
              <TermsLink
                onClick={() => window.open("/terms-and-conditions", "_blank")}
              >
                Termos e Condições{" "}
              </TermsLink>
              aplicaveis a este depósito.
            </TermsText>
          </>
        )}

        {stage === "USDT1" && (
          <>
            <Header style={{ textAlign: "center" }}>
              DEPÓSITO EM REDE <br /> USDT
            </Header>
            <div
              style={{
                width: "100%",
                height: "5px",
                backgroundColor: "#c09c5c",
                marginBottom: "20px",
              }}
            ></div>
            <SubHeader style={{ textAlign: "center" }}>
              Leia o QR Code para fazer o depósito
            </SubHeader>
            <div
              style={{
                padding: "10px",
                background: "white",
                width: "212px",
                margin: "auto",
              }}
            >
              <QRCodeSVG
                value={"0xa8f0df8fccef56ba982b366282a0b8dbf146d357"}
                size={192}
              />
            </div>

            <ButtonCopy
              onClick={() => {
                navigator.clipboard.writeText(
                  "0xa8f0df8fccef56ba982b366282a0b8dbf146d357"
                );
                toast.success("Endereço USDT copiado com sucesso!");
              }}
              style={{ backgroundColor: "#26A17B", border: "none" }}
            >
              COPIAR ENDEREÇO USDT
            </ButtonCopy>

            <Button
              onClick={() => handleCryptoDeposit("USDT")}
              style={{ backgroundColor: "#26A17B" }}
            >
              CONFIRMAR DEPÓSITO
            </Button>
            <TermsText style={{ marginTop: "0.5rem" }}>
              <TermsLink
                onClick={() => window.open("/terms-and-conditions", "_blank")}
              >
                Termos e Condições{" "}
              </TermsLink>
              aplicaveis a este depósito.
            </TermsText>
          </>
        )}

        {(stage === "BTC3" || stage === "USDT3") && (
          <>
            <Header
              style={{
                marginTop: "18rem",
                textAlign: "center",
              }}
            >
              {success ? (
                <>
                  TRANSAÇÃO ADICIONADA <span>COM SUCESSO</span>
                </>
              ) : (
                <>
                  <span style={{ color: "red" }}>ERRO</span> AO ADICIONAR
                  TRANSAÇÃO
                </>
              )}
            </Header>
            <Button onClick={close}>OK</Button>
          </>
        )}
      </Popup>
    </>
  );
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 900;
  cursor: pointer;
`;

const Popup = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: 700px;
  width: 450px;
  background-color: #121212;
  z-index: 1000;
  border-radius: 10px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.3);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: white;

  @media (max-width: 500px) {
    height: 600px;
    width: 90%;
  }
`;

const Header = styled.h2`
  font-size: 2rem;
  color: #ffffff;

  span {
    color: #c09c5c;
  }
`;

const SubHeader = styled.h4`
  font-size: 1.5rem;
  color: #ffffff;
  font-weight: bold;
  margin-bottom: 10px;

  @media (max-width: 340px) {
    font-size: 1rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  background-color: #1c1c1c;
  border: none;
  border-radius: 5px;
  color: white;
  font-size: 1rem;

  /* Estiliza o input number para remover as setas */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield; /* Para Firefox */
  }
`;

const ButtonCopy = styled.button`
  width: 50%;
  margin-inline: auto;
  padding: 10px;
  background-color: transparent;
  border: 1px solid #c09c5c;
  color: white;
  font-weight: bold;
  border-radius: 5px;
  font-size: 1rem;
  cursor: pointer;
  margin-top: -1.5rem;

  @media (max-width: 545px) {
    margin-top: -1.2rem;
  }

  &:hover {
    background-color: #c09c5c;
  }
`;

const Button = styled.button`
  padding: 10px;
  background-color: #c09c5c;
  color: #fff;
  font-weight: bold;
  border: none;
  border-radius: 5px;
  font-size: 1rem;
  cursor: pointer;
  margin-top: auto;

  &:hover {
    background-color: #8c7444;
  }
`;

const ValueButton = styled.button`
  background-color: #333333;
  color: white;
  border: none;
  padding: 10px;
  border-radius: 5px;
  cursor: pointer;
  width: 30%;
  margin-inline: auto;

  &:hover {
    background-color: #444444;
  }
`;

const TermsText = styled.p`
  font-size: 0.9rem;
  color: #fff;
  margin-top: 0px;
  text-align: center;
  margin-bottom: -20px;
`;

const TermsLink = styled.span`
  color: #2c65ff;
  cursor: pointer;
`;

const CurrencyButton = styled.button`
  padding: 15px;
  background-color: #333333;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: bold;
  transition: background-color 0.2s;

  &:hover {
    background-color: #444444;
  }
`;
